import React from 'react';
import { Link } from 'react-router-dom';
import { Play, ShoppingCart, Music, Headphones, Award } from 'lucide-react';
import { branding } from '../config/branding';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';

const Home = () => {
  const { currentTheme } = useTheme();

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            background: `radial-gradient(circle at 20% 50%, ${currentTheme.primary}40, transparent 50%), 
                        radial-gradient(circle at 80% 80%, ${currentTheme.primary}30, transparent 40%)`
          }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <div className="mb-6 inline-block">
            <img 
              src="/logos/marz-symbol.png" 
              alt="Tha Advocate Beats"
              className="w-24 h-24 mx-auto mb-4 animate-pulse object-contain"
            />
          </div>
          
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 tracking-tight">
            {branding.hero.title}
            {branding.hero.subtitle && <span className="block mt-2" style={{ color: currentTheme.primary }}>{branding.hero.subtitle}</span>}
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto">
            {branding.hero.description}
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/beats">
              <Button 
                size="lg" 
                className="h-14 px-8 text-lg font-bold text-black hover:scale-105 transition-transform"
                style={{ backgroundColor: currentTheme.primary }}
              >
                <Music className="mr-2 h-5 w-5" />
                Browse Beats
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="h-14 px-8 text-lg font-bold border-2 hover:scale-105 transition-transform"
                style={{ borderColor: currentTheme.primary, color: currentTheme.primary }}
              >
                Custom Order
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-3 rounded-full" style={{ backgroundColor: currentTheme.primary }} />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 border-y border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Music, value: branding.stats.totalBeats, label: 'Premium Beats' },
              { icon: Headphones, value: branding.stats.artistsServed, label: 'Artists Served' },
              { icon: Award, value: branding.stats.yearsExperience, label: 'Years Experience' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-8 rounded-2xl bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 group">
                <stat.icon className="w-12 h-12 mx-auto mb-4 transition-transform group-hover:scale-110" style={{ color: currentTheme.primary }} />
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Producer Profile Section */}
      <section className="py-24 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="inline-block px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
                <span className="text-sm font-bold uppercase tracking-wider" style={{ color: currentTheme.primary }}>
                  The Producer
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {branding.producer.name}
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                {branding.producer.bio}
              </p>
              
              <div className="mb-8">
                <h3 className="text-white font-bold mb-4 uppercase tracking-wider">Specialties</h3>
                <div className="flex flex-wrap gap-2">
                  {branding.producer.specialties.map((specialty, idx) => (
                    <span 
                      key={idx}
                      className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 text-sm border border-white/10"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-2 mb-8">
                {branding.producer.achievements.map((achievement, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: currentTheme.primary }} />
                    <span className="text-gray-400">{achievement}</span>
                  </div>
                ))}
              </div>

              <Link to="/collaborate">
                <Button 
                  size="lg"
                  className="h-14 px-10 text-lg font-bold text-black hover:scale-105 transition-transform"
                  style={{ backgroundColor: currentTheme.primary }}
                >
                  Work With Me
                </Button>
              </Link>
            </div>

            <div className="order-1 md:order-2">
              <div className="relative group">
                <div className="absolute inset-0 rounded-3xl opacity-20 group-hover:opacity-30 transition-opacity"
                     style={{ background: `radial-gradient(circle at center, ${currentTheme.primary}, transparent)` }} />
                <img 
                  src={branding.producer.profilePic}
                  alt={branding.producer.name}
                  className="relative w-full h-auto rounded-3xl shadow-2xl border-2 border-white/10 group-hover:border-white/20 transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative rounded-3xl overflow-hidden p-12 text-center"
               style={{ background: `linear-gradient(135deg, ${currentTheme.primary}20, ${currentTheme.secondary}80)` }}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Need Something Custom?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Work directly with {branding.producer.name} to create a unique beat tailored to your vision
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="h-14 px-10 text-lg font-bold text-black hover:scale-105 transition-transform"
                style={{ backgroundColor: currentTheme.primary }}
              >
                Start Your Project
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;